import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiGet, apiPost } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { cardsApi } from '@/features/cards/api'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import type { Card } from '@/types/entities'

export interface WorkSessionRow {
  id: string
  cardId?: string
  userId: string
  startedAt: string
  endedAt: string | null
  note: string | null
}
export interface WorkSummary {
  totalMs: number
  byUser: Array<{ userId: string; displayName: string; ms: number; running: boolean }>
  sessions: WorkSessionRow[]
  now: string
  canTime?: boolean
}
export interface ActiveWork {
  session: WorkSessionRow | null
  card: { id: string; code: string | null; title: string } | null
  now: string
}

export const workKeys = {
  active: ['worktime', 'active'] as const,
  card: (cardId: string) => ['worktime', 'card', cardId] as const,
}

/** The caller's running timer, for the header clock. `fetchedAt` pairs with the server's `now`. */
export function useActiveWork(enabled = true) {
  return useQuery({
    queryKey: workKeys.active,
    queryFn: async () => ({ ...(await apiGet<ActiveWork>('worktime/active')), fetchedAt: Date.now() }),
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  })
}

export function useCardWork(cardId: string) {
  return useQuery({
    queryKey: workKeys.card(cardId),
    queryFn: async () => ({ ...(await apiGet<WorkSummary>(`worktime/cards/${cardId}`)), fetchedAt: Date.now() }),
    refetchInterval: 30_000,
    staleTime: 10_000,
  })
}

export function useStartWork() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (cardId: string) => apiPost<{ stopped: { cardId: string } | null }>('worktime/start', { cardId }),
    onSuccess: (result, cardId) => {
      if (result.stopped && result.stopped.cardId !== cardId) toast.message('Your previous timer was stopped: one thing at a time.')
      void queryClient.invalidateQueries({ queryKey: ['worktime'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useStopWork() {
  const queryClient = useQueryClient()
  return useMutation({
    // The note is the work log entry: what got done in this stretch. Optional, never required.
    mutationFn: (note?: string) => apiPost<{ session: WorkSessionRow | null }>('worktime/stop', note ? { note } : {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['worktime'] }),
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

/** Re-renders every second while `on`, for a running clock. */
export function useTick(on: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!on) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [on])
  return now
}

/** Start a timer outside a component (a toast action), and refresh everything that shows it. */
export async function startWorkNow(queryClient: QueryClient, cardId: string): Promise<void> {
  try {
    await apiPost('worktime/start', { cardId })
  } catch (error) {
    toast.error(getErrorMessage(error))
  }
  void queryClient.invalidateQueries({ queryKey: ['worktime'] })
}

/** Finishing or sending work for approval ends its timer, so the clock does not keep running on finished work. */
export async function stopWorkIfOn(queryClient: QueryClient, cardId: string): Promise<void> {
  const active = queryClient.getQueryData<ActiveWork>(workKeys.active)
  if (!active?.session || active.session.endedAt || active.session.cardId !== cardId) return
  try {
    await apiPost('worktime/stop', {})
    toast.message('Timer stopped: this item is out of your hands now.')
  } catch {
    /* the server closes a forgotten timer at its cap; never fail the stage change over it */
  }
  void queryClient.invalidateQueries({ queryKey: ['worktime'] })
}

/** Whether `userId` may time `card`, the same rule the server applies. */
export const mayTimeCard = (card: Pick<Card, 'ownerId' | 'assigneeIds'>, userId: string): boolean =>
  Boolean(userId) && (card.ownerId === userId || (card.assigneeIds ?? []).includes(userId))

const STAGE_ORDER: Record<string, number> = { in_progress: 0, todo: 1 }

/** Open items the caller is on, most pressing first: what to offer when nothing is being timed. */
export function useMyOpenItems(enabled = true) {
  const { userId } = useIdentity()
  return useQuery({
    queryKey: ['worktime', 'mine', userId] as const,
    enabled: enabled && Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      const page = await cardsApi.query({ filter: { assigneeIds: [userId] }, limit: 100, cursor: null })
      return page.items
        .filter((c) => c.status !== 'done' && !c.archived && !c.deletedAt)
        .sort(
          (a, b) =>
            (STAGE_ORDER[a.status] ?? 2) - (STAGE_ORDER[b.status] ?? 2) ||
            (a.dueAt ? Date.parse(a.dueAt) : Infinity) - (b.dueAt ? Date.parse(b.dueAt) : Infinity),
        )
        .slice(0, 3)
    },
  })
}
